import { TILE } from '../config/tuning'
import type { Entity } from '../ecs/entity'
import type { World } from '../ecs/world'
import { makeEnemy, makeProjectile } from '../entities/templates'
import { norm } from '../core/math'
import { PALETTE } from '../render/palette'
import { Tile } from '../world/tiles'
import { hurtPlayer } from './damage'

/**
 * Enemy behaviors: deterministic FSM/steering only — no model, no learning,
 * ever (hard rule). Each behavior is readable and tuned from config.
 */
export function aiSystem(world: World, dt: number): void {
  scheduleAuditors(world, dt)

  for (const e of world.entities) {
    if (e.kind !== 'enemy' || e.dead || !e.etype || !e.ai) continue
    if (e.spawnT > 0) {
      e.spawnT -= dt
      continue
    }
    e.ai.t += dt
    if (e.ai.cooldown > 0) e.ai.cooldown -= dt
    if (e.contactT !== undefined && e.contactT > 0) e.contactT -= dt

    const player = world.nearestPlayer(e.x, e.y)

    // Dormant until sensed: no lock-on through walls, so rooms don't pile
    // against the near wall before the door ever opens. Statics (ghosts)
    // and the Auditor are born aware; getting shot wakes a pack (damage.ts).
    if (!e.awake && e.etype !== 'static' && e.etype !== 'auditor') {
      if (player && senses(world, e, player)) e.awake = true
      else continue
    }

    switch (e.etype) {
      case 'husk':
        husk(world, e, player, dt)
        break
      case 'sentry':
        sentry(world, e, player, dt)
        break
      case 'static':
        staticGhost(world, e, player, dt)
        break
      case 'foreman':
        foreman(world, e, player, dt)
        break
      case 'auditor':
        auditor(world, e, player, dt)
        break
    }
  }

  separate(world)
}

/** proximity (heard), or line of sight within sight range (seen) */
function senses(world: World, e: Entity, player: Entity): boolean {
  const cfg = world.tuning.enemies.wake
  const d2 = (player.x - e.x) ** 2 + (player.y - e.y) ** 2
  if (d2 < cfg.proximity * cfg.proximity) return true
  return d2 < cfg.sightRange * cfg.sightRange && world.map.lineOfSight(e.x, e.y, player.x, player.y)
}

/**
 * per-entity band scaling fixed at spawn (see enemies.speedBandScale), plus
 * the floor-scoped ESCALATION directive multiplier (never the auditor — it
 * has its own ramp, same as the band-scaling rule)
 */
const scaled = (world: World, e: Entity, speed: number): number =>
  speed * (e.speedMul ?? 1) * world.floorMods.enemySpeedMul

function moveToward(world: World, e: Entity, tx: number, ty: number, speed: number, dt: number): void {
  const d = norm({ x: tx - e.x, y: ty - e.y })
  e.facing = Math.atan2(d.y, d.x)
  world.map.moveCircle(e, d.x * speed * dt, d.y * speed * dt, e.wallClipped)
}

function touching(a: Entity, b: Entity): boolean {
  const r = a.radius + b.radius
  return (a.x - b.x) ** 2 + (a.y - b.y) ** 2 < r * r
}

function contactHit(world: World, e: Entity, player: Entity, damage: number, cause: string): void {
  if (e.contactT !== undefined && e.contactT > 0) return
  if (!touching(e, player)) return
  hurtPlayer(world, player, damage, cause)
  e.contactT = world.tuning.player.contactDamageCooldown
}

/** Swarm melee: straight seek with a wander jitter; pure pressure. */
function husk(world: World, e: Entity, player: Entity | null, dt: number): void {
  const cfg = world.tuning.enemies.husk
  if (!player) return
  const jx = Math.sin(e.ai!.t * 3.1 + e.id) * 14
  const jy = Math.cos(e.ai!.t * 2.7 + e.id * 1.3) * 14
  moveToward(world, e, player.x + jx, player.y + jy, scaled(world, e, cfg.speed), dt)
  contactHit(world, e, player, cfg.contactDamage, 'a husk')
}

/** Ranged: holds preferred range, strafes, fires on line of sight. */
function sentry(world: World, e: Entity, player: Entity | null, dt: number): void {
  const cfg = world.tuning.enemies.sentry
  if (!player) return
  const dist = Math.hypot(player.x - e.x, player.y - e.y)
  const los = world.map.lineOfSight(e.x, e.y, player.x, player.y)
  const speed = scaled(world, e, cfg.speed)

  if (dist < cfg.retreatRange) {
    // back away, keep facing the player
    const away = norm({ x: e.x - player.x, y: e.y - player.y })
    world.map.moveCircle(e, away.x * speed * dt, away.y * speed * dt, true)
    e.facing = Math.atan2(player.y - e.y, player.x - e.x)
  } else if (dist > cfg.preferredRange || !los) {
    moveToward(world, e, player.x, player.y, speed, dt)
  } else {
    // strafe orbit
    const tangent = norm({ x: -(player.y - e.y), y: player.x - e.x })
    const dir = e.id % 2 === 0 ? 1 : -1
    world.map.moveCircle(e, tangent.x * dir * speed * 0.5 * dt, tangent.y * dir * speed * 0.5 * dt, true)
    e.facing = Math.atan2(player.y - e.y, player.x - e.x)
  }

  if (los && dist < cfg.preferredRange * 1.35 && e.ai!.cooldown <= 0) {
    e.ai!.cooldown = cfg.fireCooldown
    const aim = norm({ x: player.x - e.x, y: player.y - e.y })
    const mx = e.x + aim.x * (e.radius + 4)
    const my = e.y + aim.y * (e.radius + 4)
    world.add(makeProjectile(world.tuning, mx, my, aim.x, aim.y, cfg.projectileSpeed, cfg.projectileDamage, 'network'))
    // muzzle flash + quiet report so incoming fire reads before the bolt does
    world.burst(mx, my, PALETTE.projEnemy, 2, 70, 0.15, 2)
    world.bus.emit('enemy_shot', {})
  }
  contactHit(world, e, player, cfg.contactDamage, 'a sentry')
}

/** Wall-ignoring ghost: denies corners; slow sinusoidal drift straight at you. */
function staticGhost(world: World, e: Entity, player: Entity | null, dt: number): void {
  const cfg = world.tuning.enemies.static
  if (!player) return
  const drift = Math.sin(e.ai!.t * 2.2 + e.id * 2.1) * 26
  const d = norm({ x: player.x - e.x, y: player.y - e.y })
  const perp = { x: -d.y, y: d.x }
  moveToward(world, e, player.x + perp.x * drift, player.y + perp.y * drift, scaled(world, e, cfg.speed), dt)
  contactHit(world, e, player, cfg.contactDamage, 'a static')
}

/**
 * Summoner: shadows its generator, boosts nearby generators' output, and
 * resurrects corpses — makes generator destruction urgent, not optional.
 */
function foreman(world: World, e: Entity, player: Entity | null, dt: number): void {
  const cfg = world.tuning.enemies.foreman
  // stay near the closest surviving generator; if none, advance on the player
  let anchor: Entity | null = null
  let bestD = Infinity
  for (const g of world.generators()) {
    const d = (g.x - e.x) ** 2 + (g.y - e.y) ** 2
    if (d < bestD) {
      bestD = d
      anchor = g
    }
  }
  const target = anchor ?? player
  if (target) {
    const dist = Math.hypot(target.x - e.x, target.y - e.y)
    if (dist > 60) moveToward(world, e, target.x, target.y, scaled(world, e, cfg.speed), dt)
  }

  // boost aura is applied in generatorSystem by scanning foremen

  // resurrect nearby corpses
  if (e.ai!.cooldown <= 0) {
    for (const c of world.entities) {
      if (c.dead || c.kind !== 'corpse' || !c.corpseOf) continue
      if ((c.x - e.x) ** 2 + (c.y - e.y) ** 2 > cfg.resurrectRadius ** 2) continue
      c.dead = true
      const revived = makeEnemy(world.tuning, c.corpseOf, c.x, c.y, undefined, world.level.band)
      revived.spawnT = 0.6
      world.add(revived)
      world.burst(c.x, c.y, PALETTE.foreman, 10, 100, 0.5)
      e.ai!.cooldown = cfg.resurrectCooldown
      break
    }
  }
  if (player) contactHit(world, e, player, cfg.contactDamage, 'a foreman')
}

/** The Auditor: unkillable, slow, relentless, drains on contact. Magic only. */
function auditor(world: World, e: Entity, player: Entity | null, dt: number): void {
  const cfg = world.tuning.enemies.auditor
  if (!player) return
  // it gets faster the longer the floor runs — stalling is not a plan; capped under player speed
  const speed = Math.min(cfg.rampCap, cfg.speed + world.levelTime * cfg.rampPerSecond)
  moveToward(world, e, player.x, player.y, speed, dt)
  if (touching(e, player) && player.hp !== undefined && !player.dead) {
    // continuous drain: bypasses invuln frames, routes death through hurtPlayer
    player.hp -= cfg.drainPerSecond * dt
    player.flash = 0.05
    if (player.hp <= 0) {
      player.hp = 1
      player.invuln = 0
      hurtPlayer(world, player, 1, 'the Auditor')
    }
  }
}

/** spawn scheduled Auditors far from the player, on floor tiles */
function scheduleAuditors(world: World, dt: number): void {
  if (world.auditorQueue.length === 0) return
  const player = world.players()[0]
  for (const q of world.auditorQueue) q.delay -= dt
  const due = world.auditorQueue.filter((q) => q.delay <= 0)
  if (due.length === 0) return
  world.auditorQueue = world.auditorQueue.filter((q) => q.delay > 0)
  for (const _ of due) {
    const spot = farFloorTile(world, player?.x ?? 0, player?.y ?? 0)
    if (!spot) continue
    const a = makeEnemy(world.tuning, 'auditor', spot.x * TILE + TILE / 2, spot.y * TILE + TILE / 2)
    a.spawnT = 1.0
    world.add(a)
    world.burst(a.x, a.y, PALETTE.auditor, 20, 140, 0.9)
    world.bus.emit('death_type_spawn', { x: a.x, y: a.y })
  }
}

function farFloorTile(world: World, px: number, py: number): { x: number; y: number } | null {
  let best: { x: number; y: number } | null = null
  let bestD = -1
  for (let i = 0; i < 60; i++) {
    const x = world.levelRng.int(1, world.map.w - 2)
    const y = world.levelRng.int(1, world.map.h - 2)
    if (world.map.at(x, y) !== Tile.Floor) continue
    const d = (x * TILE - px) ** 2 + (y * TILE - py) ** 2
    if (d > bestD) {
      bestD = d
      best = { x, y }
    }
  }
  return best
}

/** cheap pairwise separation so swarms read as a crowd, not a stack */
function separate(world: World): void {
  const es = world.entities
  for (let i = 0; i < es.length; i++) {
    const a = es[i]!
    if (a.kind !== 'enemy' || a.dead || a.etype === 'static' || a.etype === 'auditor') continue
    for (let j = i + 1; j < es.length; j++) {
      const b = es[j]!
      if (b.kind !== 'enemy' || b.dead || b.etype === 'static' || b.etype === 'auditor') continue
      const dx = b.x - a.x
      const dy = b.y - a.y
      const min = a.radius + b.radius - 2
      const d2 = dx * dx + dy * dy
      if (d2 >= min * min || d2 === 0) continue
      const d = Math.sqrt(d2)
      const push = (min - d) / 2
      const nx = dx / d
      const ny = dy / d
      a.x -= nx * push
      a.y -= ny * push
      b.x += nx * push
      b.y += ny * push
      // separation writes positions directly, so re-resolve against walls —
      // otherwise pile pressure squeezes entities through solid tiles
      world.map.resolveCircle(a)
      world.map.resolveCircle(b)
    }
  }
}
